import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { request, socket } from '../../utils';

const AUTO_SCROLL_INTERVAL_MS = 40;
const AUTO_SCROLL_STEP_PX = 1;

const COLORS = {
  bg: '#0A0E14',
  card: '#11161F',
  cardBorder: 'rgba(255,255,255,0.10)',
  hairline: 'rgba(255,255,255,0.10)',
  textPrimary: '#FFFFFF',
  textSecondary: 'rgba(255,255,255,0.78)',
  textMuted: 'rgba(255,255,255,0.5)',
  roomBadgeBg: 'rgba(255,255,255,0.09)',
  roomBadgeBorder: 'rgba(255,255,255,0.3)',
  in: '#2FE3A6',
  inTint: 'rgba(47,227,166,0.12)',
  next: '#FFB84D',
  nextTint: 'rgba(255,184,77,0.12)',
  waiting: '#8FA3D9',
  waitingTint: 'rgba(143,163,217,0.13)'
};

const FONT_SANS = "'IBM Plex Sans', sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";

const formatPatientName = (row) =>
  `${row.name_suffix ? row.name_suffix + ' ' : ''}${row.first_name || ''} ${row.last_name || ''}`.trim();

const useInjectedFont = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);
};

const groupByDoctor = (patientData) => {
  const groups = new Map();
  patientData.forEach((row) => {
    const doctorId = row.consultant?._id || 'unassigned';
    if (!groups.has(doctorId)) {
      groups.set(doctorId, {
        doctorId,
        doctorName: row.consultant?.name || 'Unassigned',
        room: row.consultant?.other_details?.opd_room,
        floor: row.consultant?.other_details?.opd_floor,
        patients: []
      });
    }
    groups.get(doctorId).patients.push(row);
  });
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      patients: [...group.patients].sort((a, b) => a.token_no - b.token_no)
    }))
    .sort((a, b) => a.doctorName.localeCompare(b.doctorName));
};

const Legend = () => (
  <Box sx={{ display: 'flex', gap: 5, alignItems: 'center' }}>
    {[
      { color: COLORS.in, label: 'In Consultation' },
      { color: COLORS.next, label: 'Next · Get Ready' },
      { color: COLORS.waiting, label: 'Waiting' }
    ].map((item) => (
      <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: '1.3rem', color: COLORS.textSecondary, fontWeight: 600 }}>
          {item.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TierRow = ({ label, accent, tint, patient, tokenSize, minWidth, dotPulse }) => {
  if (!patient) return null;
  return (
    <Box
      key={patient._id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3.5,
        px: 3,
        py: 2.2,
        borderRadius: '12px',
        bgcolor: tint,
        borderLeft: `6px solid ${accent}`,
        animation: 'rowEnter 420ms ease-out'
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_MONO,
          fontWeight: 700,
          fontSize: tokenSize,
          color: accent,
          lineHeight: 1,
          minWidth
        }}
      >
        {patient.token_no}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
          {dotPulse && (
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: accent,
                animation: 'pulseDot 1.6s ease-in-out infinite'
              }}
            />
          )}
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: '1.15rem',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: accent
            }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_SANS,
            fontSize: '2.5rem',
            fontWeight: 700,
            color: COLORS.textPrimary,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {formatPatientName(patient)}
        </Typography>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '1.3rem', color: COLORS.textSecondary, fontWeight: 500 }}>
          UHID {patient.uhid || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

const DoctorCard = ({ group, index }) => {
  const inPatient = group.patients.find((p) => p.opd_status === 'serving');
  const queued = group.patients.filter((p) => p.opd_status !== 'serving');
  const [nextPatient, ...waitingPatients] = queued;
  const hasRoom = group.room || group.floor;

  return (
    <Box
      sx={{
        bgcolor: COLORS.card,
        border: `1px solid ${COLORS.cardBorder}`,
        borderRadius: '16px',
        p: 3.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
        animation: `cardEnter 480ms ease-out ${index * 70}ms both`
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography
          sx={{ fontFamily: FONT_SANS, fontSize: '2.2rem', fontWeight: 700, color: COLORS.textPrimary }}
        >
          Dr. {group.doctorName}
        </Typography>
        {hasRoom && (
          <Box
            sx={{
              bgcolor: COLORS.roomBadgeBg,
              border: `2px solid ${COLORS.roomBadgeBorder}`,
              borderRadius: '10px',
              px: 2.2,
              py: 1,
              flexShrink: 0
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '1.85rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
                color: COLORS.textPrimary,
                whiteSpace: 'nowrap'
              }}
            >
              {group.room ? `ROOM ${group.room}` : ''}
              {group.room && group.floor ? ' · ' : ''}
              {group.floor ? `FLOOR ${group.floor}` : ''}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ height: '1px', bgcolor: COLORS.hairline }} />

      <TierRow
        label="In Consultation"
        accent={COLORS.in}
        tint={COLORS.inTint}
        patient={inPatient}
        tokenSize="5.5rem"
        minWidth="170px"
        dotPulse
      />
      <TierRow
        label="Next · Get Ready"
        accent={COLORS.next}
        tint={COLORS.nextTint}
        patient={nextPatient}
        tokenSize="4rem"
        minWidth="130px"
      />

      {waitingPatients.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: '1.15rem',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: COLORS.waiting,
              mb: 1.2
            }}
          >
            Waiting ({waitingPatients.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
            {waitingPatients.map((p) => (
              <Box
                key={p._id}
                sx={{
                  bgcolor: COLORS.waitingTint,
                  borderRadius: '10px',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.2
                }}
              >
                <Typography sx={{ fontFamily: FONT_MONO, fontWeight: 700, fontSize: '1.7rem', color: COLORS.waiting }}>
                  {p.token_no}
                </Typography>
                <Typography sx={{ fontFamily: FONT_SANS, fontSize: '1.3rem', color: COLORS.textPrimary, fontWeight: 500 }}>
                  {formatPatientName(p)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {group.patients.length === 0 && (
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: '1.4rem', color: COLORS.textMuted, py: 1 }}>
          No patients currently in queue
        </Typography>
      )}
      {group.patients.length > 0 && !inPatient && (
        <Typography sx={{ fontFamily: FONT_SANS, fontSize: '1.3rem', color: COLORS.textMuted, py: 0.5, fontWeight: 500 }}>
          No one called in yet
        </Typography>
      )}
    </Box>
  );
};

export const QueueDisplayBoard = () => {
  useInjectedFont();
  const [patientData, setPatientData] = useState([]);
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchQueue = async () => {
      const response = await request('/patient-registeration-queue-display-list', { method: 'GET' });
      if (response?.success) {
        setPatientData(response.patientData || []);
      }
    };
    fetchQueue();

    const handleQueueUpdate = (data) => setPatientData(data || []);
    socket.on('queue-update', handleQueueUpdate);
    socket.on('connect_error', fetchQueue);

    return () => {
      socket.off('queue-update', handleQueueUpdate);
      socket.off('connect_error', fetchQueue);
    };
  }, []);

  useEffect(() => {
    const clockInterval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const scrollInterval = setInterval(() => {
      if (el.scrollHeight <= el.clientHeight) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
        el.scrollTop = 0;
      } else {
        el.scrollTop += AUTO_SCROLL_STEP_PX;
      }
    }, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(scrollInterval);
  }, [patientData]);

  const doctorGroups = useMemo(() => groupByDoctor(patientData), [patientData]);

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: COLORS.bg,
        color: COLORS.textPrimary,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        p: 4,
        overflow: 'hidden'
      }}
    >
      <style>{`
        @keyframes cardEnter {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes rowEnter {
          from { opacity: 0.3; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(47,227,166,0.55); }
          50% { box-shadow: 0 0 0 9px rgba(47,227,166,0); }
        }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: '3rem',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: COLORS.textPrimary
            }}
          >
            OPD Queue
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Legend />
          </Box>
        </Box>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '2.1rem', color: COLORS.textPrimary, fontWeight: 600 }}>
          {now.toLocaleTimeString()} &nbsp;&middot;&nbsp; {now.toLocaleDateString()}
        </Typography>
      </Box>

      <Box sx={{ height: '1px', bgcolor: COLORS.hairline, mb: 3 }} />

      {doctorGroups.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontFamily: FONT_SANS, fontSize: '2rem', color: COLORS.textMuted }}>
            No patients in queue
          </Typography>
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(640px, 1fr))',
            gap: 3,
            alignContent: 'start'
          }}
        >
          {doctorGroups.map((group, index) => (
            <DoctorCard key={group.doctorId} group={group} index={index} />
          ))}
        </Box>
      )}
    </Box>
  );
};
